import { StaffPermission, User, UserRole } from '../types';

export type TourArea = 'pos' | 'products' | 'stock' | 'reports' | 'settings';

const routes: Record<TourArea, string> = {
  pos: '/pos',
  products: '/menu',
  stock: '/stock',
  reports: '/reports',
  settings: '/settings'
};

const permissionByArea: Record<TourArea, StaffPermission> = {
  pos: 'POS',
  products: 'PRODUCTS',
  stock: 'STOCK',
  reports: 'REPORTS',
  settings: 'SETTINGS'
};

const defaultPermissions: Record<NonNullable<User['accessProfile']>, StaffPermission[]> = {
  EMPLOYEE: ['POS', 'PRODUCTS', 'KITCHEN', 'DELIVERY'],
  MANAGER: ['POS', 'PRODUCTS', 'STOCK', 'REPORTS', 'SETTINGS', 'KITCHEN', 'DELIVERY'],
  COUNTER: ['POS']
};

const tourOrder: TourArea[] = ['pos', 'products', 'stock', 'reports', 'settings'];
const protectedAreas: TourArea[] = ['stock', 'reports', 'settings'];
const storageKey = (userId: string) => `flowtab-first-access-tour-v1:${userId}`;

export const getTourAreas = (user: User): TourArea[] => {
  if (user.role === UserRole.OWNER) return tourOrder;
  if (user.role !== UserRole.CASHIER) return [];
  const permissions = user.accessPermissions || (user.accessProfile ? defaultPermissions[user.accessProfile] : ['POS']);
  return tourOrder.filter(area => permissions.includes(permissionByArea[area]));
};

export const getActiveTourArea = (userId: string): TourArea | null => {
  const stage = window.localStorage.getItem(storageKey(userId));
  return tourOrder.find(area => area === stage) || null;
};

export const beginFirstAccessTour = (user: User): boolean => {
  if (!user.id || getActiveTourArea(user.id) || window.localStorage.getItem(storageKey(user.id)) === 'done') return false;
  if (getTourAreas(user)[0] !== 'pos') return false;
  window.localStorage.setItem(storageKey(user.id), 'pos');
  return true;
};

export const advanceFirstAccessTour = (user: User, area: TourArea): string | null => {
  if (getActiveTourArea(user.id) !== area) return null;
  const areas = getTourAreas(user);
  const currentIndex = areas.indexOf(area);
  if (currentIndex < 0) return null;
  const nextArea = areas[currentIndex + 1];
  window.localStorage.setItem(storageKey(user.id), nextArea || 'done');
  return nextArea && !protectedAreas.includes(nextArea) ? routes[nextArea] : '/pos';
};

export const stopFirstAccessTour = (userId: string) => {
  window.localStorage.setItem(storageKey(userId), 'done');
};

export const getPendingTourRoute = (user: User): string | null => {
  const area = getActiveTourArea(user.id);
  return area && !protectedAreas.includes(area) && getTourAreas(user).includes(area) ? routes[area] : null;
};
